'use client'

import Card, {
  CardProps,
  SupplierCardType,
} from '@/components/v3/atoms/card/card'
import { useDwellInView } from '@/hooks/use-dwell-in-view'
import type { SupplierCarouselType } from '@/utils/articles/supplier-carousel/resolve-supplier-carousel'
import { getCardHref } from '@/utils/getCardHref'
import { carouselImpressionGTMEvent } from '@/utils/gtmEvents/carouselImpressionGTMEvent'
import { useEffect, useRef } from 'react'

type TrackedCarouselCardProps = Omit<CardProps, 'type'> & {
  type: SupplierCardType
  carouselType: SupplierCarouselType
}

/**
 * An in-article carousel card that reports one `carousel_impression` once it
 * has been at least half on screen for a second — the same rule as the
 * category listing's `TrackedCard`.
 */
export const TrackedCarouselCard = ({
  carouselType,
  ...props
}: TrackedCarouselCardProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const hasDwelled = useDwellInView(ref, { threshold: 0.5, dwellMs: 1000 })
  const firedRef = useRef(false)

  useEffect(() => {
    if (!hasDwelled || firedRef.current) return
    firedRef.current = true

    carouselImpressionGTMEvent(
      props.itemId,
      getCardHref(props.type, props.path),
      props.type,
      carouselType
    )
  }, [hasDwelled, props.itemId, props.path, props.type, carouselType])

  return <Card {...props} ref={ref} />
}
